import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Copy, Eye, RefreshCw, MessageSquare, ClipboardList, Layers } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons';
import Modal from '../components/Modal';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import { api } from '../services/api';
import { Comment } from '../types';

type Filters = {
  targetType?: 'ARTICLE' | 'PODCAST' | 'COURSE';
  isPublished?: boolean;
  search?: string;
};

const clip = (text: string, max: number) => {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
};

interface CommentsProps {
  /** در بخش وبلاگ فقط نظرات مقالات نمایش داده می‌شود */
  fixedTargetType?: Filters['targetType'];
  /** بدون هدر بزرگ صفحه (وقتی داخل صفحهٔ دیگری جاسازی شده) */
  embedded?: boolean;
}

const Comments: React.FC<CommentsProps> = ({ fixedTargetType, embedded = false }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFiltered, setTotalFiltered] = useState(0);
  const [filtersState, setFiltersState] = useState<Filters>(fixedTargetType ? { targetType: fixedTargetType } : {});
  /** نوع محتوای ثابت همیشه روی فیلترها اعمال می‌شود */
  const filters = useMemo<Filters>(
    () => (fixedTargetType ? { ...filtersState, targetType: fixedTargetType } : filtersState),
    [filtersState, fixedTargetType],
  );
  const setFilters = useCallback((f: Filters) => setFiltersState(f), []);
  const [onlyRoot, setOnlyRoot] = useState(true);
  const [copiedCommentId, setCopiedCommentId] = useState<string | null>(null);
  const [replyModal, setReplyModal] = useState<{ parent: Comment } | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyAuthorName, setReplyAuthorName] = useState('');
  const [replying, setReplying] = useState(false);
  const [editModal, setEditModal] = useState<{ comment: Comment } | null>(null);
  const [editForm, setEditForm] = useState<{ editedContent: string; isPublished: boolean }>({
    editedContent: '',
    isPublished: false,
  });
  const [editSaving, setEditSaving] = useState(false);
  const [previewModal, setPreviewModal] = useState<{ comment: Comment } | null>(null);
  const [toast, setToast] = useState('');

  const fetchComments = useMemo(
    () => async (pageParam = page, filtersParam = filters, onlyRootParam = onlyRoot) => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get('/admin/comments', {
          params: {
            page: pageParam,
            limit,
            targetType: filtersParam.targetType,
            isPublished: typeof filtersParam.isPublished === 'boolean' ? filtersParam.isPublished : undefined,
            search: filtersParam.search?.trim() || undefined,
            onlyRoot: onlyRootParam,
          },
        });
        const data = response.data;
        const list = Array.isArray(data.data) ? data.data : Array.isArray(data) ? data : [];
        setComments(list);
        const meta = data?.meta;
        setTotalPages(meta?.totalPages || 1);
        setTotalFiltered(typeof meta?.total === 'number' ? meta.total : list.length);
      } catch (err: unknown) {
        const e = err as { response?: { data?: { message?: string } } };
        setError(e?.response?.data?.message || 'خطا در دریافت نظرات');
        setComments([]);
        setTotalFiltered(0);
      } finally {
        setLoading(false);
      }
    },
    [page, limit, filters, onlyRoot],
  );

  useEffect(() => {
    void fetchComments(page, filters, onlyRoot);
  }, [fetchComments, page, filters, onlyRoot]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 3200);
  }, []);

  const activeFilterCount = [
    fixedTargetType ? undefined : filters.targetType,
    typeof filters.isPublished === 'boolean' ? filters.isPublished : undefined,
    filters.search?.trim(),
  ].filter((v) => v !== undefined && v !== '').length;

  const setFiltersAndPageOne = useCallback(
    (f: Filters) => {
      setPage(1);
      setFilters(f);
    },
    [setFilters],
  );

  const copyPhone = async (comment: Comment) => {
    if (!comment.authorPhone?.trim()) {
      showToast('شماره تماسی ثبت نشده است.');
      return;
    }
    try {
      await navigator.clipboard.writeText(comment.authorPhone);
      setCopiedCommentId(comment.id);
      window.setTimeout(() => setCopiedCommentId(null), 1200);
    } catch {
      showToast('کپی ناموفق بود.');
    }
  };

  const openEditModal = (c: Comment) => {
    setEditModal({ comment: c });
    setEditForm({
      editedContent: (c.editedContent || c.content || '').trim(),
      isPublished: c.isPublished,
    });
  };

  const submitEditModal = async () => {
    if (!editModal) return;
    setEditSaving(true);
    try {
      await api.patch(`/admin/comments/${editModal.comment.id}`, {
        editedContent: editForm.editedContent,
        isPublished: editForm.isPublished,
      });
      setEditModal(null);
      void fetchComments(page, filters, onlyRoot);
      showToast('متن نظر و وضعیت انتشار ذخیره شد.');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      alert(e?.response?.data?.message || 'خطا در ذخیره');
    } finally {
      setEditSaving(false);
    }
  };

  const deleteComment = async (comment: Comment) => {
    if (!window.confirm('حذف این نظر؟ در صورت داشتن وابستگی‌ها ممکن است زنجیر پاسخ هم حذف شود.')) return;
    try {
      await api.delete(`/admin/comments/${comment.id}`);
      void fetchComments(page, filters, onlyRoot);
      showToast('نظر حذف شد.');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      alert(e?.response?.data?.message || 'خطا در حذف نظر');
    }
  };

  const openReplyModal = (parent: Comment) => {
    if (parent.parentId) return;
    setReplyContent('');
    setReplyAuthorName('');
    setReplyModal({ parent });
  };

  const submitReply = async () => {
    if (!replyModal) return;
    if (!replyContent.trim()) {
      alert('لطفاً متن پاسخ را وارد کنید');
      return;
    }
    try {
      setReplying(true);
      await api.post(`/admin/comments/${replyModal.parent.id}/reply`, {
        content: replyContent.trim(),
        authorName: replyAuthorName.trim() || undefined,
      });
      setReplyModal(null);
      setReplyContent('');
      void fetchComments(page, filters, onlyRoot);
      showToast('پاسخ رسمی منتشر شد و زیر نظر کاربر در سایت دیده می‌شود.');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      alert(e?.response?.data?.message || 'خطا در ثبت پاسخ');
    } finally {
      setReplying(false);
    }
  };

  const targetFa = (c: Comment) =>
    c.targetType === 'ARTICLE' ? 'مقاله' : c.targetType === 'PODCAST' ? 'پادکست' : 'دوره';

  const bodyText = (c: Comment) => (c.editedContent || c.content || '').trim();

  return (
    <div className={embedded ? 'space-y-5' : 'space-y-5 p-4 sm:p-6'}>
      <div className={embedded ? 'hidden' : 'rounded-2xl bg-white p-6 shadow-sm'}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 text-indigo-700">
              <FontAwesomeIcon icon={faCommentDots} className="h-6 w-6" aria-hidden />
            </span>
            <div>
              <h1 className="text-2xl font-black text-gray-900">مدیریت نظرات</h1>
              <p className="mt-1 text-sm leading-7 text-gray-600">
                تأیید و ویرایش بازخورد کاربران؛ پاسخ رسمی دوره همین‌جا منتشر شده و کنار نظر کاربر در سایت نمایش داده می‌شود.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void fetchComments(page, filters, onlyRoot)}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-indigo-700"
          >
            {loading ? <BrandSpinnerRing size="sm" inverse /> : <RefreshCw className="h-4 w-4" aria-hidden />}
            بروزرسانی لیست
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50 to-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-indigo-700">کل نتایج (طبق فیلتر)</p>
              <p className="mt-2 text-2xl font-black text-indigo-900">{totalFiltered.toLocaleString('fa-IR')}</p>
            </div>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-100 text-indigo-700">
              <Layers className="h-4 w-4" />
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-sky-100 bg-gradient-to-b from-sky-50 to-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-sky-700">آیتم در این صفحه</p>
              <p className="mt-2 text-2xl font-black text-sky-900">{comments.length.toLocaleString('fa-IR')}</p>
            </div>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-sky-100 text-sky-700">
              <ClipboardList className="h-4 w-4" />
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-violet-100 bg-gradient-to-b from-violet-50 to-white p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold text-violet-700">نمای لیست</p>
              <p className="mt-2 text-lg font-black text-violet-950">{onlyRoot ? 'فقط نظر اصلی' : 'شامل پاسخ‌ها'}</p>
              <p className="mt-1 text-[11px] font-medium text-violet-800/90">صف تأیید و پاسخ مدرس پیشنهادی: نظر اصلی</p>
            </div>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-violet-100 text-violet-700">
              <MessageSquare className="h-4 w-4" />
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-4 shadow-sm sm:grid-cols-2 xl:grid-cols-6">
        <div className="sm:col-span-2 xl:col-span-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setPage(1);
              setFilters({ isPublished: false });
            }}
            className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800 hover:bg-amber-100"
          >
            منتظر تأیید
          </button>
          <button
            type="button"
            onClick={() => {
              setPage(1);
              setFilters({ isPublished: true });
            }}
            className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800 hover:bg-emerald-100"
          >
            منتشر شده
          </button>
          {!fixedTargetType && <button
            type="button"
            onClick={() => {
              setPage(1);
              setFilters({ targetType: 'COURSE' });
            }}
            className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-bold text-blue-800 hover:bg-blue-100"
          >
            دوره‌ها
          </button>}
          <button
            type="button"
            onClick={() => {
              setPage(1);
              setFilters({});
            }}
            className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-bold text-slate-700 hover:bg-slate-100"
          >
            همه نظرها
          </button>
          {activeFilterCount > 0 ? (
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-black text-slate-600">
              {activeFilterCount.toLocaleString('fa-IR')} فیلتر
            </span>
          ) : null}
        </div>

        <input
          className="xl:col-span-2 rounded-xl border border-gray-200 px-3 py-2 text-sm"
          placeholder="جستجو در نام، تلفن، متن نظر..."
          value={filters.search || ''}
          onChange={(e) => setFiltersAndPageOne({ ...filters, search: e.target.value })}
        />

        {!fixedTargetType && <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={filters.targetType || ''}
          onChange={(e) =>
            setFiltersAndPageOne({
              ...filters,
              targetType: (e.target.value || undefined) as Filters['targetType'],
            })
          }
        >
          <option value="">نوع محتوا: همه</option>
          <option value="COURSE">دوره</option>
          <option value="ARTICLE">مقاله</option>
          <option value="PODCAST">پادکست</option>
        </select>}

        <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={
            typeof filters.isPublished === 'boolean' ? (filters.isPublished ? 'published' : 'unpublished') : ''
          }
          onChange={(e) => {
            const val = e.target.value;
            setFiltersAndPageOne({
              ...filters,
              isPublished:
                val === 'published' ? true : val === 'unpublished' ? false : undefined,
            });
          }}
        >
          <option value="">وضعیت انتشار: همه</option>
          <option value="published">منتشر شده</option>
          <option value="unpublished">در انتظار تأیید</option>
        </select>

        <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-xs font-bold text-indigo-900">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
            checked={onlyRoot}
            onChange={(e) => {
              setOnlyRoot(e.target.checked);
              setPage(1);
            }}
          />
          فقط نظر اصلی
        </label>

        <button
          type="button"
          onClick={() => {
            setPage(1);
            setFilters({});
          }}
          className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
        >
          پاک‌کردن فیلترها
        </button>
      </div>

      {toast ? (
        <div className="fixed bottom-6 left-6 z-[200] rounded-2xl border border-white/10 bg-slate-900 px-5 py-3 text-sm font-bold text-white shadow-xl">
          {toast}
        </div>
      ) : null}

      <Modal isOpen={Boolean(replyModal)} onClose={() => !replying && setReplyModal(null)} title={fixedTargetType === 'ARTICLE' ? 'پاسخ رسمی تحریریه' : 'پاسخ رسمی مدرس'} size="large">
        {replyModal ? (
          <div className="space-y-4" dir="rtl">
            <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4">
              <p className="text-xs font-black text-slate-500">نظر کاربر</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="font-black text-slate-900">{replyModal.parent.authorName}</span>
                {replyModal.parent.rating != null && replyModal.parent.rating >= 1 ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-black text-amber-900">
                    {replyModal.parent.rating}★
                  </span>
                ) : null}
                {replyModal.parent.targetType === 'COURSE' ? (
                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-black text-indigo-800">
                    {replyModal.parent.courseTitle?.trim() || replyModal.parent.targetId.slice(0, 8)}
                  </span>
                ) : null}
                {replyModal.parent.targetType === 'ARTICLE' && replyModal.parent.articleTitle ? (
                  <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-black text-violet-800">
                    {clip(replyModal.parent.articleTitle, 50)}
                  </span>
                ) : null}
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-slate-700">
                {replyModal.parent.editedContent || replyModal.parent.content}
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-black text-gray-700">نام نمایشی زیر پاسخ (اختیاری)</label>
              <input
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none ring-indigo-500 focus:ring-2"
                placeholder="در صورت خالی از نام حساب مدیر استفاده می‌شود"
                value={replyAuthorName}
                onChange={(e) => setReplyAuthorName(e.target.value)}
                maxLength={80}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-black text-gray-700">متن پاسخ</label>
              <textarea
                className="min-h-[140px] w-full rounded-xl border border-gray-200 px-3 py-2 text-sm leading-7 outline-none ring-indigo-500 focus:ring-2"
                placeholder="متن رسمی؛ بلافاصله در سایت منتشر می‌شود."
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                disabled={replying || !replyContent.trim()}
                onClick={() => void submitReply()}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-black text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
              >
                {replying ? 'در حال ثبت…' : 'ثبت و انتشار'}
              </button>
              <button
                type="button"
                disabled={replying}
                onClick={() => setReplyModal(null)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                انصراف
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        isOpen={Boolean(editModal)}
        onClose={() => !editSaving && setEditModal(null)}
        title="ویرایش نظر و وضعیت انتشار"
        size="large"
      >
        {editModal ? (
          <div className="space-y-4" dir="rtl">
            <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-xs text-slate-600">
              <span className="font-black">{editModal.comment.authorName}</span>
              {editModal.comment.authorPhone ? (
                <>
                  {' '}
                  · <span dir="ltr">{editModal.comment.authorPhone}</span>
                </>
              ) : null}
              <span className="mx-2">·</span>
              <span>{targetFa(editModal.comment)}</span>
            </div>
            <div>
              <label className="mb-1 block text-xs font-black text-gray-700">متن (پس از ویرایش برای کاربر نمایش می‌گیرد)</label>
              <textarea
                className="min-h-[160px] w-full rounded-xl border border-gray-200 px-3 py-2 text-sm leading-8"
                value={editForm.editedContent}
                onChange={(e) => setEditForm((p) => ({ ...p, editedContent: e.target.value }))}
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-black text-gray-700">انتشار در سایت</label>
              <div className="flex flex-wrap gap-3">
                <label className="inline-flex items-center gap-2 text-sm font-bold text-gray-800">
                  <input
                    type="radio"
                    checked={editForm.isPublished === true}
                    onChange={() => setEditForm((p) => ({ ...p, isPublished: true }))}
                  />
                  منتشر شود
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-bold text-gray-800">
                  <input
                    type="radio"
                    checked={editForm.isPublished === false}
                    onChange={() => setEditForm((p) => ({ ...p, isPublished: false }))}
                  />
                  منتشر نشود
                </label>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
              <button
                type="button"
                disabled={editSaving}
                onClick={() => void submitEditModal()}
                className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-black text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {editSaving ? 'در حال ذخیره…' : 'ذخیره تغییرات'}
              </button>
              <button
                type="button"
                disabled={editSaving}
                onClick={() => setEditModal(null)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 hover:bg-gray-50"
              >
                انصراف
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      {previewModal ? (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/35 p-4 backdrop-blur-[2px]"
          onClick={() => setPreviewModal(null)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white p-5 shadow-2xl"
            role="dialog"
            aria-modal
            onClick={(e) => e.stopPropagation()}
            dir="rtl"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-lg font-black text-slate-900">متن کامل نظر</h3>
              <button
                type="button"
                onClick={() => setPreviewModal(null)}
                className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-black text-slate-600 hover:bg-slate-50"
              >
                بستن
              </button>
            </div>
            <p className="mb-2 text-xs font-bold text-slate-500">{previewModal.comment.authorName}</p>
            <div className="max-h-[65vh] overflow-y-auto whitespace-pre-wrap rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-8 text-slate-800">
              {bodyText(previewModal.comment)}
            </div>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl bg-white p-4 shadow-sm">
        {loading ? (
          <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 via-white to-white">
            <div className="flex items-center justify-between border-b border-indigo-100/70 px-4 py-3">
              <div className="flex items-center gap-2 text-indigo-700">
                <BrandSpinnerRing size="sm" />
                <span className="text-sm font-black">در حال بارگذاری نظرات</span>
              </div>
            </div>
            <div className="space-y-2 p-3">
              {Array.from({ length: 8 }).map((_, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5 animate-pulse"
                >
                  <div className="col-span-3 h-4 rounded bg-slate-200" />
                  <div className="col-span-2 h-4 rounded bg-slate-200" />
                  <div className="col-span-4 h-4 rounded bg-slate-200" />
                  <div className="col-span-3 h-8 rounded-xl bg-indigo-100/80" />
                </div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-4 text-sm font-bold text-red-800">{error}</div>
        ) : comments.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 py-14 text-center">
            <FontAwesomeIcon icon={faCommentDots} className="mx-auto h-14 w-14 text-indigo-200" />
            <p className="mt-4 text-base font-black text-gray-900">هیچ نظری با این فیلتر نیست</p>
            <p className="mt-2 text-sm text-gray-600">یکی از پیش‌مجموعه‌های بالا یا فیلتر انتشار را تغییر دهید.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-xl border border-gray-100">
              <table className="min-w-[960px] w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-right font-bold text-gray-700">کاربر و تماس</th>
                    <th className="px-4 py-3 text-right font-bold text-gray-700">نوع محتوا</th>
                    <th className="min-w-[10rem] px-4 py-3 text-right font-bold text-gray-700">عنوان / هدف</th>
                    <th className="px-4 py-3 text-right font-bold text-gray-700">وضعیت</th>
                    <th className="px-4 py-3 text-center font-bold text-gray-700">امتیاز</th>
                    <th className="min-w-[14rem] px-4 py-3 text-right font-bold text-gray-700">متن</th>
                    <th className="px-4 py-3 text-right font-bold text-gray-700 whitespace-nowrap">تاریخ</th>
                    <th className="px-4 py-3 text-right font-bold text-gray-700">عملیات</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {comments.map((c) => {
                    const isReply = Boolean(c.parentId);
                    const text = bodyText(c);
                    return (
                      <tr
                        key={c.id}
                        className={`align-top hover:bg-gray-50/80 ${isReply ? 'bg-violet-50/40 border-r-4 border-indigo-500' : ''}`}
                      >
                        <td className="px-4 py-3">
                          <p className="font-black text-gray-900">{c.authorName}</p>
                          <div className="mt-1 flex items-center gap-2" dir="ltr">
                            <span className="text-xs text-gray-700">{c.authorPhone || '—'}</span>
                            {c.authorPhone ? (
                              <button
                                type="button"
                                title="کپی شماره"
                                onClick={() => void copyPhone(c)}
                                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </button>
                            ) : null}
                          </div>
                          {copiedCommentId === c.id ? (
                            <p className="mt-1 text-[10px] font-bold text-emerald-600">کپی شد</p>
                          ) : null}
                          {isReply ? (
                            <span className="mt-2 inline-flex rounded-full bg-violet-200 px-2 py-0.5 text-[10px] font-black text-violet-900">
                              پاسخ رسمی مدرس
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-800">
                            {targetFa(c)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-bold leading-6 text-slate-800">
                          {c.targetType === 'COURSE'
                            ? c.courseTitle?.trim() || `شناسه: ${clip(c.targetId, 14)}`
                            : c.targetType === 'ARTICLE' && c.articleTitle
                              ? clip(c.articleTitle, 60)
                              : clip(c.targetId, 36)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-[11px] font-black ${
                              c.isPublished ? 'bg-emerald-100 text-emerald-900' : 'bg-amber-100 text-amber-950'
                            }`}
                          >
                            {c.isPublished ? 'منتشر' : 'در انتظار'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center text-amber-500">
                          {typeof c.rating === 'number' && c.rating >= 1 ? '★'.repeat(c.rating) : '—'}
                        </td>
                        <td className="max-w-[20rem] px-4 py-3 text-xs leading-7 text-gray-800">
                          <p className="line-clamp-3">{clip(text, 260)}</p>
                          <button
                            type="button"
                            onClick={() => setPreviewModal({ comment: c })}
                            className="mt-2 inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-black text-slate-700 hover:bg-slate-50"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            پیش‌نمایش کامل
                          </button>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500">
                          {c.createdAt ? new Date(c.createdAt).toLocaleString('fa-IR') : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-2">
                            {!isReply ? (
                              <button
                                type="button"
                                onClick={() => openReplyModal(c)}
                                className="rounded-xl bg-emerald-600 px-3 py-2 text-center text-xs font-black text-white shadow-sm hover:bg-emerald-700"
                              >
                                پاسخ
                              </button>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => openEditModal(c)}
                              className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2 text-center text-xs font-black text-indigo-950 hover:bg-indigo-100"
                            >
                              ویرایش
                            </button>
                            <button
                              type="button"
                              onClick={() => void deleteComment(c)}
                              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center text-xs font-black text-red-700 hover:bg-red-100"
                            >
                              حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col justify-between gap-3 border-t border-gray-100 pt-4 text-sm font-bold text-gray-600 sm:flex-row sm:items-center">
              <span>
                صفحه {page.toLocaleString('fa-IR')} از {totalPages.toLocaleString('fa-IR')}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2 font-black hover:bg-gray-50 disabled:opacity-40"
                >
                  قبلی
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-xl border border-gray-300 bg-white px-4 py-2 font-black hover:bg-gray-50 disabled:opacity-40"
                >
                  بعدی
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Comments;
